import { DIALOG_DATA } from '@angular/cdk/dialog'
import { ScrollingModule } from '@angular/cdk/scrolling'

import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, inject, OnDestroy, OnInit, signal } from '@angular/core'
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { dateValidator } from '@core/validators/date.validator'
import {
  PalantirAvatarComponent,
  PalantirBannerComponent,
  PalantirButtonComponent, PalantirContentComponent,
  PalantirDialogComponent,
  PalantirDialogRef,
  PalantirIconComponent,
  PalantirInputComponent,
  PalantirListItemComponent, PalantirProgressComponent,
  PalantirRadioComponent,
  PalantirSkeletonComponent,
  PalantirTextComponent
} from '@fieldcontrol/palantir'
import { Company, WarrantyTemplate } from '@generated/sammathnaur.graphql'
import { TranslateModule } from '@ngx-translate/core'
import { addDays, format } from 'date-fns'
import { NgScrollbarModule } from 'ngx-scrollbar'
import { WarrantiesService } from 'projects/fce-components/src/app/core/api/warranties/warranties.service'
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs'
import { CompaniesService } from '../../services/companies.service'

@Component({
  selector: 'app-warranties-dialog',
  templateUrl: './warranties-dialog.component.html',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    PalantirTextComponent,
    PalantirAvatarComponent,
    PalantirListItemComponent,
    PalantirContentComponent,
    PalantirDialogComponent,
    PalantirButtonComponent,
    ScrollingModule,
    NgScrollbarModule,
    PalantirIconComponent,
    MatProgressSpinnerModule,
    PalantirInputComponent,
    TranslateModule,
    PalantirRadioComponent,
    PalantirProgressComponent,
    PalantirBannerComponent,
    PalantirSkeletonComponent
],
  schemas: [
    CUSTOM_ELEMENTS_SCHEMA
  ]
})
export class WarrantiesDialogComponent implements OnInit, OnDestroy {
  private readonly data = inject(DIALOG_DATA)
  private readonly palantirDialogRef = inject(PalantirDialogRef)
  public readonly warrantiesService = inject(WarrantiesService)
  private readonly companiesService = inject(CompaniesService)
  private destroy$ = new Subject<void>()

  public warrantiesTemplates = signal<WarrantyTemplate[]>([])
  public companies = signal<Company[]>([])
  public selectedCompany = signal<Company | null>(null)
  public selectedWarrantyTemplate = signal<WarrantyTemplate | null>(null)
  public progress = signal<number>(0)
  public step = signal<number>(1)
  private baseProgress = 100 / 3

  public searchControl = new FormControl('')
  public searchCompaniesControl = new FormControl('')

  public form!: FormGroup

  public isSelected = (id: string) => computed(() => this.selectedWarrantyTemplate()?.id === id)
  public isCompanySelected = (id: string) => computed(() => this.selectedCompany()?.id === id)

  public async ngOnInit(): Promise<void> {
    this.form = new FormGroup({
      warrantyTemplateId: new FormControl(null, [Validators.required]),
      equipmentId: new FormControl(this.data.equipmentId, [Validators.required]),
      coverageStartedAt: new FormControl('', [Validators.required, dateValidator()]),
      coverageCompletedAt: new FormControl({ value: '', disabled: true }),
      companyId: new FormControl(null),
    })
    this.warrantiesService.filterForm.reset({ nameContains: null, type: 'equipments' })
    this.companiesService.filterForm.reset({ nameContains: null, archived: false })
    this.searchControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(async () => {
      await this.search()
    })

    this.searchCompaniesControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(async () => {
      await this.searchCompany()
    })

    this.form.get('coverageStartedAt')?.valueChanges.pipe(
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe((value) => {
      if (!this.selectedWarrantyTemplate()) {
        return
      }

      const coverageDurationDays = this.selectedWarrantyTemplate()?.coverageDurationDays || 0
      const extendedCoverageDurationDays = this.selectedWarrantyTemplate()?.extendedCoverageDurationDays || 0
      const coverageDaysWarranty = coverageDurationDays + extendedCoverageDurationDays
      const endDate = format(addDays(new Date(value), coverageDaysWarranty), 'yyyy-MM-dd')
      this.form.get('coverageCompletedAt')?.setValue(endDate)

      if (this.form.valid && this.step() === 3) {
        this.progress.set(this.baseProgress * 3)
      }
    })

    await this.search()
  }

  public ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  public async close(): Promise<void> {
    await this.palantirDialogRef.close()
  }

  public toggleWarrantyTemplate(warrantyTemplate: WarrantyTemplate): void {
    this.selectedWarrantyTemplate.set(warrantyTemplate)
    this.form.get('warrantyTemplateId')?.setValue(warrantyTemplate.id)
    this.setEquipmentInstalledAt()
    this.progress.set(this.baseProgress * this.step())
  }

  public async save(): Promise<void> {
    this.progress.set(this.baseProgress * 3)
    this.warrantiesService.isLoading.set(true)
    await this.warrantiesService.create(this.form.value)
    await this.palantirDialogRef.close({ reload: true })
  }

  public async next(): Promise<void> {
    if (this.form.valid) {
      this.progress.set(this.baseProgress * 3)
    }

    this.step.set(this.step() + 1)

    if (this.step() === 2) {
      await this.searchCompany()
    }

    if (this.step() === 3) {
      this.form.get('companyId')?.setValue(this.selectedCompany()?.id)
    }

    if (this.form.invalid) {
      this.progress.set(this.baseProgress * this.step())
    }

  }

  private setEquipmentInstalledAt() {
    const { equipment } = this.data
    const installedAt = equipment?.installedAt ? format(new Date(equipment.installedAt), 'yyyy-MM-dd') : ''
    if (installedAt) {
      this.form.get('coverageStartedAt')?.setValue(installedAt)
    }
  }

  public trackByFn(index: number): number {
    return index
  }

  public toggleCompany(company: Company): void {
    this.selectedCompany.set(company)
    this.form.get('companyId')?.setValue(company.id)
    this.progress.set(this.baseProgress * this.step())
  }

  private async searchCompany(): Promise<void> {
    this.companiesService.filterForm.patchValue({ nameContains: this.searchCompaniesControl.value })
    this.companiesService.isLoading.set(true)
    const result = await this.companiesService.searchPage()
    this.companies.set(result?.data?.companies?.items ?? [])
    this.companiesService.isLoading.set(false)
  }

  private async search(): Promise<void> {
    this.warrantiesService.filterForm.patchValue({ nameContains: this.searchControl.value, type: 'equipments' })
    this.warrantiesService.isLoading.set(true)
    const result = await this.warrantiesService.searchPage()
    this.warrantiesTemplates.set((result.data?.warrantiesTemplates?.items ?? []) as WarrantyTemplate[])
    this.warrantiesService.isLoading.set(false)
  }
}
